import { useState } from 'react';
import type { LeadStatus } from '@embrace-returns/shared';
import { api } from '../api/client';

// Config-driven recipient domains (UX gating only — closeLead enforces server-side
// against the same list). Safe default of embracefs.co.uk if the var is unset.
// Empty-aware: a blank var would yield [] (reject everyone). Treat empty/
// whitespace the same as unset — fall back to embracefs.co.uk.
const rawDomains = import.meta.env.VITE_ALLOWED_RECIPIENT_DOMAINS;
const ALLOWED_RECIPIENT_DOMAINS = (
  rawDomains && rawDomains.trim() ? rawDomains : 'embracefs.co.uk'
)
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

export const recipientValid = (email: string) => {
  const m = /^[^@\s]+@([^@\s]+)$/.exec(email.trim().toLowerCase());
  return !!m && ALLOWED_RECIPIENT_DOMAINS.includes(m[1]);
};

type NotifyChoice = 'referrer' | 'other' | 'none';

export interface PassOnResult {
  leadId: string;
  status: LeadStatus;
  closedAt?: string;
  notified?: boolean;
  emailFailed?: boolean;
}

/**
 * The shared three-option pass-on flow (notify referrer / another adviser / don't
 * notify), used by the Lead Detail sticky bar and the Inbox per-row modal. Owns the
 * choice, recipient validation, close state, and the close API call.
 */
export function PassOnPanel({
  leadId,
  ownerEmail,
  onDone,
  onCancel,
}: {
  leadId: string;
  /** The lead's embraceAdviserEmail — drives the referrer option + its gate. */
  ownerEmail: string;
  onDone: (result: PassOnResult) => void;
  onCancel: () => void;
}) {
  const referrerAvailable = recipientValid(ownerEmail);
  const [choice, setChoice] = useState<NotifyChoice>(
    referrerAvailable ? 'referrer' : 'none',
  );
  const [otherEmail, setOtherEmail] = useState('');
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    if (choice === 'other' && !recipientValid(otherEmail)) {
      setError('Enter an email on an allowed recipient domain.');
      return;
    }
    setClosing(true);
    setError(null);
    try {
      const res = await api.leads.close(leadId, {
        notify: choice,
        ...(choice === 'other' ? { recipientEmail: otherEmail.trim() } : {}),
      });
      onDone(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update this lead.');
    } finally {
      setClosing(false);
    }
  };

  return (
    <div>
      <p className="text-[12.5px] text-muted">Who should be notified?</p>

      <div className="mt-3 space-y-2.5">
        {referrerAvailable && (
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="radio"
              name="notify"
              className="mt-0.5"
              checked={choice === 'referrer'}
              onChange={() => setChoice('referrer')}
            />
            <span className="text-[13px] text-ink">
              Notify the original referrer
              <span className="block text-[12px] text-muted">{ownerEmail}</span>
            </span>
          </label>
        )}

        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="radio"
            name="notify"
            className="mt-0.5"
            checked={choice === 'other'}
            onChange={() => setChoice('other')}
          />
          <span className="flex-1 text-[13px] text-ink">
            Notify another Embrace adviser
            {choice === 'other' && (
              <input
                type="email"
                autoFocus
                value={otherEmail}
                onChange={(e) => setOtherEmail(e.target.value)}
                placeholder="adviser@embracefs.co.uk"
                className="mt-1.5 block w-full max-w-xs rounded-lg border border-hair px-2.5 py-1.5 text-[13px] text-ink focus:border-navy focus:outline-none"
              />
            )}
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="radio"
            name="notify"
            className="mt-0.5"
            checked={choice === 'none'}
            onChange={() => setChoice('none')}
          />
          <span className="text-[13px] text-ink">
            Don’t notify
            <span className="block text-[12px] text-muted">
              Claimed it yourself — just close it.
            </span>
          </span>
        </label>
      </div>

      {error && (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-600">
          {error}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={closing}
          className="rounded-lg border border-hair px-4 py-2 text-[13px] font-medium text-muted transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={confirm}
          disabled={closing || (choice === 'other' && !recipientValid(otherEmail))}
          className="rounded-lg bg-brand px-5 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {closing ? 'Saving…' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}
